import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmt, fmtDate, DEBT_CATEGORIES, catColor } from "@/lib/constants";
import { Zap, Snowflake, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, Cell, LabelList, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import UtilizationSimulator from "@/components/debts/UtilizationSimulator";
import BiweeklyLab from "@/components/debts/BiweeklyLab";
import BalanceTransferLab from "@/components/debts/BalanceTransferLab";
import HelpTip from "@/components/HelpTip";

export default function Simulator() {
  const [debts, setDebts] = useState([]);
  const [strategy, setStrategy] = useState("avalanche");
  const [extra, setExtra] = useState(0);
  const [sim, setSim] = useState(null);
  const [ready, setReady] = useState(false);
  const [plan, setPlan] = useState(null);
  const [credit, setCredit] = useState(null);

  const loadPlan = () => api.get("/payoff-plan").then(r => setPlan(r.data));
  const simReq = useRef(0);
  useEffect(() => {
    Promise.all([api.get("/debts"), api.get("/payoff-plan"), api.get("/debts/progress")]).then(([d, p, pr]) => {
      setDebts(d.data.filter(x => x.balance > 0));
      setStrategy(p.data.strategy);
      setExtra(p.data.snowball_amount);
      setPlan(p.data);
      setCredit(pr.data.credit);
      setReady(true);
    });
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!debts.length) { setSim(null); return; }
    const reqId = ++simReq.current;
    const t = setTimeout(() => {
      api.post("/debts/simulate", { strategy, extra_monthly_payment: parseFloat(extra) || 0 })
        .then(r => { if (reqId === simReq.current) setSim(r.data); })
        .catch(() => { if (reqId === simReq.current) toast.error("Simulation failed"); });
    }, 250);
    return () => clearTimeout(t);
  }, [ready, debts.length, strategy, extra]);

  const costSplitChart = useMemo(() => {
    if (!sim?.chosen?.per_debt) return [];
    return sim.chosen.per_debt
      .filter(d => debts.some(x => x.id === d.id))
      .map(d => ({ name: d.name, principal: d.orig_balance, interest: d.total_interest }));
  }, [sim, debts]);

  const costTotals = useMemo(() => {
    const p = costSplitChart.reduce((s, d) => s + d.principal, 0);
    const i = costSplitChart.reduce((s, d) => s + d.interest, 0);
    return { principal: p, interest: i, pct: p + i > 0 ? Math.round((i / (p + i)) * 100) : 0 };
  }, [costSplitChart]);

  const timelineChart = useMemo(() => {
    if (!sim?.chosen?.per_debt) return [];
    const months = sim.chosen.months || [];
    return sim.chosen.per_debt
      .filter(d => d.paid_off_month && debts.some(x => x.id === d.id))
      .map(d => ({
        name: d.name,
        month: d.paid_off_month,
        date: months[d.paid_off_month - 1]?.date,
        color: catColor(DEBT_CATEGORIES, (debts.find(x => x.id === d.id) || {}).category),
      }))
      .sort((a, b) => a.month - b.month);
  }, [sim, debts]);

  return (
    <div className="space-y-6" data-testid="simulator-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-violet-400 font-semibold">What-if Lab</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Payoff Simulator</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ display: debts.length ? undefined : "none" }}>
        <div className="lg:col-span-2 p-5 rounded-2xl border border-border bg-card">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex flex-wrap bg-accent p-1 rounded-xl border border-border">
              <button onClick={() => setStrategy("snowball")} data-testid="sim-strategy-snowball"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${strategy === "snowball" ? "bg-cyan-500 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}>
                <Snowflake className="h-4 w-4" /> Snowball
              </button>
              <button onClick={() => setStrategy("avalanche")} data-testid="sim-strategy-avalanche"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${strategy === "avalanche" ? "bg-violet-600 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}>
                <Zap className="h-4 w-4" /> Avalanche
              </button>
              <button onClick={() => setStrategy("cfi")} data-testid="sim-strategy-cfi"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${strategy === "cfi" ? "bg-emerald-600 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}>
                <Wallet className="h-4 w-4" /> Cash Flow Index
              </button>
            </div>
            <div className="text-xs text-muted-foreground max-w-md">
              {strategy === "snowball"
                ? "Pay smallest balances first — quick wins keep motivation high."
                : strategy === "cfi"
                ? "Pay the debt that frees the most monthly cash flow first (balance ÷ minimum payment)."
                : "Pay highest APR first — mathematically saves the most interest."}
            </div>
          </div>
          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1">Extra monthly payment (what-if) <HelpTip k="extra_payment" /></label>
            <div className="flex items-center gap-3 mt-1">
              <input type="range" min={0} max={2000} step={25} value={extra} onChange={e => setExtra(parseFloat(e.target.value))} data-testid="sim-extra-slider" className="flex-1 accent-violet-500" />
              <input type="number" min={0} value={extra} onChange={e => setExtra(parseFloat(e.target.value || 0))} data-testid="sim-extra-input"
                className="w-24 px-2 py-1 rounded-md border border-border bg-background text-sm font-mono text-right" />
              <span className="text-xs text-muted-foreground">/ mo</span>
            </div>
          </div>
        </div>
        <div className="p-5 rounded-2xl border border-border bg-card grid grid-cols-2 gap-3">
          <Stat label="Current debt" value={fmt(debts.reduce((s, d) => s + d.balance, 0))} />
          <Stat label="Debt-free in" value={sim ? `${sim.chosen.totals.months_to_debt_free} mo` : "—"} testid="sim-months" />
          <Stat label="Total interest" value={sim ? fmt(sim.chosen.totals.total_interest) : "—"} tone="rose" />
          <Stat label="Interest saved" value={sim ? fmt(sim.savings_vs_minimums.interest_saved) : "—"} tone="emerald" hint="vs no extra payment" />
        </div>
      </div>

      {!debts.length && (
        <div className="p-8 rounded-2xl border border-dashed border-border text-center text-muted-foreground text-sm">
          No debts yet. <Link to="/debts" className="text-teal-500 underline">Add a debt</Link> to unlock the simulator.
        </div>
      )}

      {sim && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Where your money goes" subtitle={`Principal vs interest per debt — ${fmt(costTotals.interest)} (${costTotals.pct}%) of your payments is pure interest`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costSplitChart} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" interval={0} />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                <Tooltip cursor={{ fill: "hsl(var(--accent))", opacity: 0.3 }}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12, color: "hsl(var(--foreground))" }}
                  labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                  itemStyle={{ color: "hsl(var(--foreground))" }}
                  formatter={(v, n) => [fmt(v), n === "principal" ? "Principal" : "Interest"]} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v === "principal" ? "Principal" : "Interest"} />
                <Bar dataKey="principal" stackId="cost" fill="#14B8A6" radius={[0, 0, 0, 0]} data-testid="sim-cost-principal" />
                <Bar dataKey="interest" stackId="cost" fill="#F43F5E" radius={[6, 6, 0, 0]} data-testid="sim-cost-interest" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="When each debt is gone" subtitle={`Month each debt is fully paid off on ${ { snowball: "Snowball", avalanche: "Avalanche", cfi: "Cash Flow Index" }[strategy] }`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timelineChart} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${v}mo`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={90} />
                <Tooltip cursor={{ fill: "hsl(var(--accent))", opacity: 0.3 }}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12, color: "hsl(var(--foreground))" }}
                  labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                  itemStyle={{ color: "hsl(var(--foreground))" }}
                  formatter={(v, n, p) => [`Paid off in ${v} months${p.payload.date ? ` · ${fmtDate(p.payload.date)}` : ""}`, p.payload.name]} />
                <Bar dataKey="month" radius={[0, 6, 6, 0]} data-testid="sim-timeline-bars">
                  <LabelList dataKey="date" position="right" formatter={(d) => d ? fmtDate(d) : ""} style={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  {timelineChart.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {debts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <BiweeklyLab />
          <BalanceTransferLab />
        </div>
      )}
      <UtilizationSimulator credit={credit} plan={plan} onPlanChanged={loadPlan} />
    </div>
  );
}

function Stat({ label, value, tone, hint, testid }) {
  const toneCls = { rose: "text-rose-500", emerald: "text-emerald-500" }[tone] || "";
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`font-display font-mono font-extrabold text-xl mt-1 ${toneCls}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
function ChartCard({ title, subtitle, children }) {
  return (
    <div className="p-5 rounded-2xl border border-border bg-card">
      <div className="mb-3">
        <div className="font-display font-bold text-lg">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <div className="h-72">{children}</div>
    </div>
  );
}
